import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {
  InterfaceEventsEnum,
  SchedulingSectionsEnum,
  trackInterfaceEvent,
} from '../../../../../../libs/shared/core/src/services/tracking/tracking';
import {
  ComposerPanelId,
  ViewerPanelId,
} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {DropdownProps} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {SimpleDropdown} from '../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdown';
import {SimpleDropdownItem} from '../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdownItem';
import {SchedulingLinkModel} from '../../../../../models/api/schedulingLinkModel';
import {CalendarsEmptyContainer} from '../../../../calendars/empty/calendarsEmptyContainer';
import {MeetingTimesData} from '../../scheduling/meetingsettings/meetingSettingsPanel';
import {SchedulingLinksDropdownContainer} from '../../scheduling/schedulingLinksDropdownContainer';

/*
 * Props.
 */

export interface ComposerSchedulingDropdowPublicProps extends DropdownProps {
  panelId: ComposerPanelId | ViewerPanelId;
  onInsertMeetingTimesSelect: () => void;
  onInsertMeetingTimes: (data: MeetingTimesData) => void;
  onInsertLink: (model: SchedulingLinkModel) => void;
}

interface ComposerSchedulingDropdownProps extends ComposerSchedulingDropdowPublicProps {
  hasNoCalendars: boolean;
}

/*
 * Styles.
 */

const StyledEmptyWrapperDiv = styled.div`
  padding: 0px 10px;
`;

/*
 * Component.
 */

export const ComposerSchedulingDropdown: FC<ComposerSchedulingDropdownProps> = (props) => {
  const {
    panelId,
    onInsertMeetingTimesSelect,
    onInsertMeetingTimes,
    onInsertLink,
    hasNoCalendars,
    ...dropdownProps
  } = props;
  const [isSchedulingLinksDropdownOpen, setIsSchedulingLinksDropdownOpen] = useState(false);

  function wrapOnRequestClose<T>(handler: (arg: T) => void) {
    return (arg: T) => {
      // Close the popover
      if (dropdownProps.onRequestClose) {
        dropdownProps.onRequestClose();
      }

      return handler(arg);
    };
  }

  if (isSchedulingLinksDropdownOpen) {
    return (
      <SchedulingLinksDropdownContainer
        {...dropdownProps}
        panelId={panelId}
        backButtonLabel={<Trans id="composer-schedulinglinks-dropdown-back">Back to scheduling</Trans>}
        onInsertMeetingTimes={wrapOnRequestClose(onInsertMeetingTimes)}
        onSchedulingLinkInsertLink={wrapOnRequestClose(onInsertLink)}
        onBackButtonClick={() => setIsSchedulingLinksDropdownOpen(false)}
      />
    );
  }

  return (
    <SimpleDropdown {...dropdownProps}>
      {renderDropdownContent(props, () => setIsSchedulingLinksDropdownOpen(true))}
    </SimpleDropdown>
  );
};

/*
 * Helpers.
 */

function renderDropdownContent(
  props: ComposerSchedulingDropdownProps,
  onViewSchedulingLinksSelect: () => void,
) {
  if (props.hasNoCalendars) {
    return (
      <StyledEmptyWrapperDiv>
        <CalendarsEmptyContainer
          connectPrompt={
            <Trans id="nocalendars-connect-schedulemeetings">
              Connect your work email account to schedule meetings faster.
            </Trans>
          }
          renderReauthPrompt={(channelName) => (
            <Trans id="nocalendars-reauth-schedulemeetings">
              Add calendar permissions to your {channelName} inbox to schedule meetings faster.
            </Trans>
          )}
        />
      </StyledEmptyWrapperDiv>
    );
  }

  return (
    <>
      <SimpleDropdownItem
        key="scheduling-insert-times"
        onSelect={() => {
          trackInterfaceEvent(InterfaceEventsEnum.SCHEDULING_COMPOSER_VIEW, {
            section: SchedulingSectionsEnum.MEETING_TIMES,
          });

          props.onInsertMeetingTimesSelect();
        }}
      >
        <Trans id="scheduling-insert-times">Insert meeting times</Trans>
      </SimpleDropdownItem>
      <SimpleDropdownItem
        key="scheduling-view-links"
        onSelect={(e) => {
          trackInterfaceEvent(InterfaceEventsEnum.SCHEDULING_COMPOSER_VIEW, {
            section: SchedulingSectionsEnum.SCHEDULING_LINK,
          });

          e.preventDefault();
          onViewSchedulingLinksSelect();
        }}
      >
        <Trans id="scheduling-view-links">View scheduling links</Trans>
      </SimpleDropdownItem>
    </>
  );
}
